import { Head } from "@inertiajs/react"
import { PublicShell } from "@/components/PublicShell"
import type { PublicLocale } from "@/lib/public-links"

const COPY = {
  en: {
    title: "Data licence",
    description: "Open-data attribution and licence terms for location data published by Xplore Croatia, including OpenStreetMap-derived records.",
    kicker: "Open data",
    intro: "This page explains which parts of Xplore Croatia's published location data use OpenStreetMap and how that data may be reused.",
    osmTitle: "OpenStreetMap attribution",
    osmBody: "Some published place identities and coordinates are based on or checked against OpenStreetMap data. OpenStreetMap data is © OpenStreetMap contributors and is available under the Open Database Licence 1.0 (ODbL).",
    databaseTitle: "Applicable database rights",
    databaseBody: "The database rights in OpenStreetMap-derived portions of Xplore Croatia's published place database, including the collective Rab restaurant location dataset, are made available under ODbL 1.0. Independently written editorial text, operator contact facts, and other independently sourced content are not relicensed by this notice.",
    downloadTitle: "Machine-readable access",
    downloadBody: "The current published Rab restaurant location dataset is available as GeoJSON. The response includes its attribution and licence metadata.",
    download: "Download Rab restaurant GeoJSON",
    osmCopyright: "OpenStreetMap copyright and licence",
    odbl: "ODbL 1.0 licence text",
  },
  de: {
    title: "Datenlizenz",
    description: "Quellenangaben und Lizenzbedingungen für veröffentlichte Standortdaten von Xplore Croatia, einschließlich OpenStreetMap-Daten.",
    kicker: "Offene Daten",
    intro: "Diese Seite erklärt, welche veröffentlichten Standortdaten von Xplore Croatia OpenStreetMap nutzen und wie diese Daten weiterverwendet werden dürfen.",
    osmTitle: "OpenStreetMap-Quellenangabe",
    osmBody: "Einige veröffentlichte Ortsidentitäten und Koordinaten beruhen auf OpenStreetMap-Daten oder wurden mit ihnen abgeglichen. Die OpenStreetMap-Daten stehen unter © OpenStreetMap-Mitwirkende und sind unter der Open Database Licence 1.0 (ODbL) verfügbar.",
    databaseTitle: "Anwendbare Datenbankrechte",
    databaseBody: "Die Datenbankrechte an den von OpenStreetMap abgeleiteten Teilen der veröffentlichten Ortsdatenbank von Xplore Croatia, einschließlich des zusammengeführten Standortdatensatzes der Restaurants auf Rab, werden unter ODbL 1.0 bereitgestellt. Eigenständig verfasste redaktionelle Texte, Kontaktdaten der Betreiber und andere unabhängig beschaffte Inhalte werden durch diesen Hinweis nicht neu lizenziert.",
    downloadTitle: "Maschinenlesbarer Zugriff",
    downloadBody: "Der aktuelle veröffentlichte Standortdatensatz der Restaurants auf Rab ist als GeoJSON verfügbar. Die Antwort enthält Angaben zu Quelle und Lizenz.",
    download: "GeoJSON der Restaurants auf Rab herunterladen",
    osmCopyright: "Urheberrecht und Lizenz von OpenStreetMap",
    odbl: "Lizenztext der ODbL 1.0",
  },
} satisfies Record<PublicLocale, Record<string, string>>

export default function DataLicence({
  locale,
  alternate_paths,
  rab_restaurants_geojson_path,
}: {
  locale: PublicLocale
  alternate_paths: Record<PublicLocale, string>
  rab_restaurants_geojson_path: string
}) {
  const copy = COPY[locale]

  return (
    <>
      <Head title={copy.title}>
        <meta name="description" content={copy.description} />
        <meta property="og:title" content={copy.title} />
        <meta property="og:description" content={copy.description} />
      </Head>
      <PublicShell locale={locale} localePaths={alternate_paths}>
        <main className="public-container py-10">
          <article className="mx-auto max-w-3xl">
            <p className="public-kicker">{copy.kicker}</p>
            <h1 className="mt-2">{copy.title}</h1>
            <p className="mt-4">{copy.intro}</p>

            <section className="mt-8">
              <h2>{copy.osmTitle}</h2>
              <p className="mt-3">{copy.osmBody}</p>
              <div className="mt-3 flex flex-wrap gap-4">
                <a href="https://www.openstreetmap.org/copyright">{copy.osmCopyright}</a>
                <a href="https://opendatacommons.org/licenses/odbl/1-0/">{copy.odbl}</a>
              </div>
            </section>

            <section className="mt-8">
              <h2>{copy.databaseTitle}</h2>
              <p className="mt-3">{copy.databaseBody}</p>
            </section>

            <section className="mt-8 rounded-xl border border-hairline bg-surface p-5">
              <h2>{copy.downloadTitle}</h2>
              <p className="mt-3">{copy.downloadBody}</p>
              <a className="btn btn-secondary mt-4" href={rab_restaurants_geojson_path}>{copy.download}</a>
            </section>
          </article>
        </main>
      </PublicShell>
    </>
  )
}
